/* ============================================================
   KBN — Widerruf-Formular → Klaviyo Event Bridge

   Feuert beim Submit der Widerruf-Form (§356a Abs. 4 BGB
   Bestätigungsseite) ein "Widerruf Submitted" Event direkt an
   Klaviyo's Client Events API. Klaviyo-Flow triggert daraufhin
   eine Eingangsbestätigung an den Kunden (rechtlich vorgeschrieben,
   "unverzüglich auf dauerhaftem Datenträger").

   Architektur:
     - Form submit fires → diese JS-Hook lÄuft parallel zur normalen
       Shopify-Contact-Form Submission
     - 2 Fetch-Calls mit keepalive:true (überleben Page-Navigation):
         1. POST /client/profiles  → Customer-Identify
         2. POST /client/events    → Event mit allen Form-Daten +
            Language-Property für Multi-Lingual Routing im Template
     - Fehler werden silent gelogged, blockieren niemals das Submit
       (Shopify-Owner-Mail muss IMMER durchgehen, Klaviyo ist
       Best-Effort-Layer für die Kunden-Confirmation)

   Klaviyo-Setup:
     - Flow "Widerruf Submitted" mit ONE Email-Action
     - Dynamic Content im Template mit `{% case event.language %}` für
       Multi-Language. event.language kommt aus dem Form-Submit (das ist
       die zuverlässigste Sprache — rechtlich relevant für §356a).
     - Email-Action als TRANSACTIONAL markieren (sonst Smart Sending
       cancelt die Mail wenn der Kunde in den letzten 16h was bekommen
       hat → silent drop, NICHT compliance-konform)

   Worker-Bridge (OPTIONAL):
     Wenn WORKER_BASE_URL gesetzt ist, läuft parallel zur Klaviyo-
     Event-Direktfeuerung auch ein POST an den Bridge-Worker
     /sync-locale. Der Worker updated dann Shopify customer.locale
     (= Source of Truth) und Klaviyo profile.locale → diese werden
     für Smart-Translations in ANDEREN Mails verwendet (Marketing,
     Welcome). Falls WORKER_BASE_URL leer → keine Worker-Calls,
     Verhalten wie vor der Worker-Migration.

   Public-Key: SQscPg (= shop kitchen-by-nature.com), bereits im Theme
   sichtbar in layout/theme.liquid — sicher zu exposen.
   ============================================================ */
(function(){
  'use strict';

  if (window.KBN_WIDERRUF_KLAVIYO_INIT) return;
  window.KBN_WIDERRUF_KLAVIYO_INIT = true;

  var KLAVIYO_COMPANY_ID = 'SQscPg';
  var KLAVIYO_REVISION   = '2024-10-15';
  var EVENT_NAME         = 'Widerruf Submitted';
  var FORM_SELECTOR      = '.wf-form';

  // Bridge-Worker URL. Nach Deploy des Workers (siehe _kbn-bridge/README.md)
  // hier die echte URL eintragen.
  // Leerer String = Worker NICHT aktiv → fallback auf direkten Klaviyo-Call.
  var WORKER_BASE_URL    = 'https://kbn-bridge.kitchen-by-nature.workers.dev';

  // ----- Form-Reader -----
  // Liest alle relevanten Felder aus dem Form-Element per name-Attribut.
  // Fallback auf leeren String wenn Feld fehlt — der API-Call läuft
  // trotzdem, optionale Felder können legal leer sein (z.B. Artikel-
  // Beschreibung bei Vollwiderruf).
  function getField(form, name){
    var el = form.querySelector(
      'input[name="contact[' + name + ']"],' +
      'textarea[name="contact[' + name + ']"],' +
      'select[name="contact[' + name + ']"]'
    );
    return el ? (el.value || '').trim() : '';
  }

  function readForm(form){
    var lang = (getField(form, 'Sprache') || 'en').toLowerCase();
    return {
      email           : getField(form, 'email'),
      firstName       : getField(form, 'Vorname'),
      lastName        : getField(form, 'Nachname'),
      street          : getField(form, 'Straße'),
      zip             : getField(form, 'PLZ'),
      city            : getField(form, 'Ort'),
      country         : getField(form, 'Land'),
      orderNumber     : getField(form, 'Bestellnummer'),
      orderDate       : getField(form, 'Bestelldatum'),
      revocationType  : getField(form, 'Art des Widerrufs'),
      items           : getField(form, 'Artikel'),
      declarationText : getField(form, 'Widerrufserklärung'),
      receivedAtHuman : getField(form, 'Eingangsdatum'),
      receivedAtISO   : getField(form, 'Eingangsdatum ISO'),
      language        : lang,
      locale          : getField(form, 'Sprachregion') || lang
    };
  }

  // Eingangs-Timestamp WIRD NICHT von JS überschrieben.
  // Begründung: §356a Abs. 4 BGB verlangt "Datum und Uhrzeit des Eingangs" als
  // rechtsverbindlich. Server-Zeit (Shopify-Liquid `{{ "now" | date }}`) ist
  // die trader-authoritative Quelle — Browser-Zeit könnte vom Kunden verstellt
  // sein und wäre vor Gericht angreifbar.
  // Differenz zwischen Page-Render und tatsächlichem Submit liegt typisch bei
  // <3 Minuten (Form-Ausfüll-Zeit) und ist juristisch unkritisch.
  // Diese Funktion ist Stub geworden, behalte sie für API-Backwards-Compat.
  function updateSubmitTimestamps(form){
    // no-op: server-rendered timestamps from Liquid are authoritative
    return;
  }

  // ----- Payload-Builder -----

  // Profile-Update: stamped das STANDARD-TOP-LEVEL Klaviyo-Attribut
  // `locale` mit der bevorzugten Sprache des Kunden. DAS ist die Quelle
  // die Klaviyo Smart Translations liest.
  //
  // KRITISCH: locale muss `data.attributes.locale` sein, NICHT
  // `data.attributes.properties.Locale`. Custom-Properties mit
  // demselben Namen wie Standard-Klaviyo-Properties werden
  // SHADOWED (= ignoriert für Segmentierung & Smart Translations).
  // Vorher hatten wir `properties.Locale` gesetzt, das hat nichts
  // bewirkt weil Shopify's Klaviyo-Integration parallel das echte
  // $locale via Shopify-Customer-Sync auf "de" gesetzt hat.
  //
  // Format: BCP-47 ("de-DE", "en-DE"). Klaviyo strippt unsupported
  // Country-Varianten automatisch zur Base-Language ("en-DE" → "en")
  // wenn keine "en-DE"-Translation existiert.
  //
  // Zusätzlich: `properties.lang` als ISO 639-1 Backup, falls
  // Smart Translations mal explizit auf eine Custom-Property
  // umgestellt wird, ODER für interne Segmentierungs-Filter.
  function buildProfilePayload(d){
    return {
      data: {
        type: 'profile',
        attributes: {
          email: d.email,
          first_name: d.firstName,
          last_name: d.lastName,
          locale: d.locale,
          location: {
            address1: d.street,
            city: d.city,
            zip: d.zip,
            country: d.country
          },
          properties: {
            lang: d.language,
            LastWiderrufAt: d.receivedAtISO
          }
        }
      }
    };
  }

  // Event-Payload: das ist das, worauf der Klaviyo-Flow triggert.
  // ALLE Form-Felder als event.properties — Template referenziert
  // sie mit {{ event.<key> }}.
  // language-Key MUSS lowercase sein (Klaviyo conditions sind
  // case-sensitive: 'de' !== 'DE' !== 'de-DE').
  function buildEventPayload(d){
    var fullName = (d.firstName + ' ' + d.lastName).replace(/\s+/g, ' ').trim();
    var addressFull = [
      d.street,
      [d.zip, d.city].filter(Boolean).join(' '),
      d.country
    ].filter(Boolean).join(', ');

    return {
      data: {
        type: 'event',
        attributes: {
          metric: {
            data: { type: 'metric', attributes: { name: EVENT_NAME } }
          },
          properties: {
            // Routing
            language: d.language,
            locale: d.locale,
            // Customer
            customer_name: fullName,
            first_name: d.firstName,
            last_name: d.lastName,
            email: d.email,
            // Address
            address_street: d.street,
            address_zip: d.zip,
            address_city: d.city,
            address_country: d.country,
            address_full: addressFull,
            // Order
            order_number: d.orderNumber,
            order_date: d.orderDate,
            // Withdrawal
            revocation_type: d.revocationType,
            items: d.items,
            declaration_text: d.declarationText,
            // Audit
            received_at: d.receivedAtISO,
            received_at_human: d.receivedAtHuman,
            source: 'widerruf-form-v1'
          },
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: d.email,
                first_name: d.firstName,
                last_name: d.lastName,
                locale: d.locale,
                properties: {
                  lang: d.language
                }
              }
            }
          }
        }
      }
    };
  }

  // ----- Fetch -----
  // keepalive:true sorgt dafür dass die Request auch dann komplett
  // durchgeht wenn die Seite navigiert (= Form-Submit triggert
  // typischerweise eine Page-Navigation zur Success-Page).
  function postToKlaviyo(path, body){
    return fetch(
      'https://a.klaviyo.com/client/' + path + '/?company_id=' + KLAVIYO_COMPANY_ID,
      {
        method: 'POST',
        keepalive: true,
        headers: {
          'revision': KLAVIYO_REVISION,
          'content-type': 'application/json',
          'accept': 'application/json'
        },
        body: JSON.stringify(body)
      }
    ).catch(function(err){
      // Silent fail: niemals das Form-Submit blockieren.
      console.warn('[KBN Widerruf→Klaviyo]', path, err);
    });
  }

  // Bridge-Worker call (optional). Updated Shopify customer.locale +
  // Klaviyo profile.locale serverseitig — Shopify ist Source of Truth,
  // Klaviyo Shopify-Sync trägt's automatisch nach.
  function postToWorker(path, body){
    if (!WORKER_BASE_URL) return; // Worker nicht konfiguriert → skip
    return fetch(WORKER_BASE_URL + path, {
      method: 'POST',
      keepalive: true,
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body)
    }).catch(function(err){
      // Silent fail: Worker ist nice-to-have, Klaviyo-Direct hat schon
      // die wichtigen Daten gepushed (Event + Event.language).
      console.warn('[KBN Widerruf→Worker]', path, err);
    });
  }

  // ----- Submit-Handler -----
  function handleSubmit(form){
    try {
      // Eingangs-Timestamp auf Submit-Zeit aktualisieren BEVOR wir lesen,
      // damit Klaviyo-Event und Owner-Mail dieselbe Submit-Zeit zeigen.
      updateSubmitTimestamps(form);

      var data = readForm(form);

      // Ohne E-Mail kein Identify/Event möglich — Form fängt das
      // ohnehin per required-Attribute ab, hier nur Defensive.
      if (!data.email) return;

      // Alle Calls feuern parallel. keepalive:true ist KRITISCH —
      // ohne das würde der Browser die Requests beim Page-Unload
      // abbrechen und das Event käme nie an.
      // - Klaviyo /client/profiles: Profile-Identify + Custom Properties
      // - Klaviyo /client/events:   Event mit event.language für
      //                              Dynamic-Content Routing im Template
      // - Worker /sync-locale (optional): Shopify customer.locale +
      //                              Klaviyo profile.locale persistent
      //                              setzen für Smart-Translations in
      //                              ANDEREN Mails (Marketing).
      postToKlaviyo('profiles', buildProfilePayload(data));
      postToKlaviyo('events',   buildEventPayload(data));
      postToWorker('/sync-locale', {
        email: data.email,
        locale: data.locale,
        source: 'widerruf-form',
      });
    } catch (e) {
      console.warn('[KBN Widerruf→Klaviyo] handler error:', e);
    }
  }

  // ----- DOM-Bind -----
  function attach(){
    var forms = document.querySelectorAll(FORM_SELECTOR);
    forms.forEach(function(form){
      if (form.dataset.kbnKlaviyoBound === '1') return;
      form.dataset.kbnKlaviyoBound = '1';
      // capture:false, NICHT preventDefault — wir wollen NUR feuern,
      // dann läuft der normale Shopify-Form-Submit weiter.
      form.addEventListener('submit', function(){ handleSubmit(form); });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attach);
  } else {
    attach();
  }
})();
